import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2, Bell } from "lucide-react";
import { toast } from "sonner";
import api from "@/services/api";
import { useAuth } from "@/context/AuthContext";
import { subscribeReadySW } from "@/services/push";

/**
 * Forced onboarding modal shown when a logged-in user has no `city` set
 * (typical for Google/Gmail signups). Blocks the rest of the UI until a
 * city is picked from the autocomplete, then persists it on the user
 * profile + refreshes the auth context.
 *
 * Renders nothing when the user already has a city.
 */
export default function CityOnboardingModal() {
  const { user, refreshUser } = useAuth();
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [alerts, setAlerts] = useState(true);
  const debounceRef = useRef(null);

  const needsOnboarding = Boolean(user && !user.city);

  useEffect(() => {
    if (!needsOnboarding) return;
    const q = query.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.geoForward(q, 8);
        setSuggestions(res?.suggestions || []);
      } catch {
        setSuggestions([]);
      }
    }, 280);
  }, [query, needsOnboarding]);

  const handlePick = async (hit) => {
    if (!user) return;
    // If alerts are checked, fire the native permission prompt FIRST (before any
    // profile save) so iOS/Android show the system pop-up on this user gesture.
    let pushGranted = false;
    if (alerts && typeof Notification !== "undefined" && "serviceWorker" in navigator) {
      try {
        pushGranted = (await Notification.requestPermission()) === "granted";
      } catch (err) {
        console.error("requestPermission failed", err);
      }
    }
    setIsSaving(true);
    try {
      await api.updateUser(user.id, {
        city: hit.city,
        city_lat: hit.lat,
        city_lng: hit.lng,
      });
      await api.updateMySettings({ notify_radius: alerts });
      if (pushGranted) {
        subscribeReadySW().catch((err) => console.error("subscribe failed", err));
      }
      await refreshUser();
      toast.success(`Bienvenue ! Ta ville : ${hit.city}`);
    } catch (err) {
      toast.error(err.message || "Impossible d'enregistrer la ville");
    } finally {
      setIsSaving(false);
    }
  };

  if (!needsOnboarding) return null;

  return (
    <div
      data-testid="city-onboarding-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 11000,
        background: "rgba(0,0,0,0.92)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      <div style={{
        width: "100%", maxWidth: 380,
        background: "#0a0a0a", borderRadius: 18, border: "1px solid #1f1f1f",
        padding: "28px 22px 22px",
      }}>
        <div style={{ textAlign: "center", marginBottom: 18 }}>
          <div style={{
            width: 56, height: 56, borderRadius: "50%",
            background: "linear-gradient(135deg,#22c55e 0%,#16a34a 100%)",
            margin: "0 auto 14px", display: "flex",
            alignItems: "center", justifyContent: "center",
            boxShadow: "0 6px 18px rgba(34,197,94,0.35)",
          }}>
            <MapPin size={28} color="#fff" />
          </div>
          <h2 className="font-heading" style={{ fontSize: 20, fontWeight: 800, color: "#fff" }}>
            Choisis ta ville
          </h2>
          <p style={{ fontSize: 13, color: "#9ca3af", marginTop: 6, lineHeight: 1.45 }}>
            On l'utilise pour te montrer les matchs autour de toi et t'envoyer
            des alertes a 20 km.
          </p>
        </div>

        <div style={{ position: "relative" }}>
          <MapPin size={16} style={{
            position: "absolute", left: 14, top: "50%",
            transform: "translateY(-50%)", color: "#22c55e",
          }} />
          <input
            data-testid="onboarding-city-input"
            type="text"
            autoFocus
            value={query}
            placeholder="Ville"
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            style={{
              height: 48, width: "100%",
              paddingLeft: 40, paddingRight: 14,
              borderRadius: 12, background: "#111",
              border: "1px solid #333", color: "#fff",
              fontSize: 14, outline: "none",
            }}
          />
        </div>

        {suggestions.length > 0 && (
          <ul data-testid="onboarding-suggestions" style={{
            marginTop: 8, listStyle: "none", padding: 4,
            background: "#0a0a0a", border: "1px solid #1a1a1a",
            borderRadius: 12, maxHeight: 260, overflow: "auto",
          }}>
            {suggestions.map((h, idx) => (
              <li
                key={`${idx}-${h.city}-${h.lat}`}
                data-testid={`onboarding-sugg-${h.city}`}
                onClick={() => !isSaving && handlePick(h)}
                style={{
                  padding: "11px 12px", borderRadius: 8, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 8,
                  color: "#fff", fontSize: 13,
                  opacity: isSaving ? 0.6 : 1,
                }}
                className="hover:bg-[#1a1a1a]"
              >
                <MapPin size={13} style={{ color: "#22c55e", flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{h.city}</span>
                <span style={{ fontSize: 10, color: "#6b7280" }}>
                  {h.lat.toFixed(2)}, {h.lng.toFixed(2)}
                </span>
              </li>
            ))}
          </ul>
        )}

        {isSaving && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 8, marginTop: 12, color: "#9ca3af", fontSize: 12,
          }}>
            <Loader2 size={14} className="animate-spin" /> Enregistrement...
          </div>
        )}

        <label
          data-testid="onboarding-alerts-checkbox"
          style={{
            display: "flex", alignItems: "center", gap: 10, marginTop: 16,
            padding: "12px 14px", borderRadius: 12, cursor: "pointer",
            background: alerts ? "rgba(34,197,94,0.10)" : "#111",
            border: alerts ? "1px solid rgba(34,197,94,0.4)" : "1px solid #2a2a2a",
          }}
        >
          <input
            type="checkbox"
            checked={alerts}
            onChange={(e) => setAlerts(e.target.checked)}
            style={{ width: 18, height: 18, accentColor: "#22c55e", flexShrink: 0 }}
          />
          <Bell size={15} color={alerts ? "#22c55e" : "#6b7280"} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: "#d1d5db", fontWeight: 600 }}>
            Recevoir les alertes de mes matchs
          </span>
        </label>

        <p style={{
          marginTop: 14, fontSize: 11, color: "#4b5563", textAlign: "center",
        }}>
          France et Geneve supportes. Tu pourras la changer plus tard depuis ton profil.
        </p>
      </div>
    </div>
  );
}
